// GoogleMap's props (BL-018, the maps design v3, package 4): what goes on top of Google's map, as our
// own drawing. The visits, routes and shop are DayMap's (schedule-schemas.ts), so a screen that draws
// DayMap today hands GoogleMap the same answer; the technicians' dots are new (place 1: where each is
// now, from their iPad, only while on duty). A route is drawn straight from pin to pin, between our own
// points, never along another company's road lines (the design's terms check).
import { z } from 'zod';
import { DayMapProps, MAP_PIN_STATES } from './schedule-schemas.ts';

/**
 * Where a point came from (location_points_preferred's source). Only google is Google's: shown on
 * Google's map only, never on the stand-in (Google's terms, 3.2.3(e)). The rest are ours: the import's,
 * a person's pin (office), where the iPad arrived (arrival), our own address lookup's (routing).
 */
export const POINT_SOURCES = ['imported', 'google', 'office', 'arrival', 'routing'] as const;
export type PointSource = (typeof POINT_SOURCES)[number];

const Id = z.string().min(1);
const Lat = z.number().min(-90).max(90);
const Lng = z.number().min(-180).max(180);
const Pen = z.int().min(1).max(8).nullable();

export const GoogleMapData = z.object({
  /**
   * The visits, as DayMap's, and one more kind: place, an address on its own with no visit (the job
   * sheet's and the customer record's map, package 6), drawn in ink. Each carries where its point came
   * from (location_points_preferred's source): Google's map draws every one, the stand-in only ours.
   */
  pins: z.array(
    DayMapProps.shape.pins.element.extend({
      state: z.enum([...MAP_PIN_STATES, 'place']),
      source: z.enum(POINT_SOURCES),
    }),
  ),
  routes: DayMapProps.shape.routes,
  shop: DayMapProps.shape.shop,
  /** Where each technician on duty is now, from their iPad; none off duty. */
  techs: z.array(
    z.object({
      id: Id,
      lat: Lat,
      lng: Lng,
      pen: Pen,
      /** Their short name: "Alex P." */
      name: z.string(),
      /** How long since their iPad last said where it was ("12 min ago"); null when it's just now. */
      ago: z.string().nullable(),
      /** No word from the iPad for a while (no signal): drawn dashed, "last seen". */
      stale: z.boolean(),
      /** Which way they're heading, in degrees from north; null when standing still or not known. */
      heading: z.number().min(0).max(360).nullable(),
    }),
  ),
});
export type GoogleMapData = z.infer<typeof GoogleMapData>;
